import React, { createContext, useContext, useState } from "react";

// Initial course data
const initialCourseData = {
  aboutCourse:
    "This is an introductory course in Computer Science that covers fundamental programming concepts, algorithms, and data structures. Students will learn problem-solving approaches, fundamental programming concepts, and the basics of software development. The course includes hands-on coding exercises and practical projects.",

  creditPoints: {
    lecture: 30,
    tutorial: 15,
    practical: 25,
    project: 30,
  },

  learningOutcomes: [
    "LO1: Demonstrate understanding of fundamental programming concepts and principles",
    "LO2: Apply problem-solving techniques to develop algorithmic solutions",
    "LO3: Design and implement basic software applications using modern programming languages",
    "LO4: Analyze and debug programs using professional development tools",
    "LO5: Understand and implement fundamental data structures and algorithms",
  ],

  // New weekly plan data
  weeklyPlan: [
    {
      weekNumber: 1,
      topics: [
        "Weather apps",
        "Digital assistants",
        "Software that analyzes data to optimize a given business function",
      ],
    },
    {
      weekNumber: 2,
      topics: [
        "Weather apps",
        "Digital assistants",
        "Software that analyzes data to optimize a given business function",
      ],
    },
    {
      weekNumber: 3,
      topics: [
        "Weather apps",
        "Digital assistants",
        "Software that analyzes data to optimize a given business function",
      ],
    },
  ],

  // syllabus (normalized shape)
  syllabus: {
    modules: [
    {
      moduleNumber: 1,
      moduleTitle: "Introduction to Programming",
      topics: [
        "Basic Programming Concepts",
        "Variables and Data Types",
        "Control Structures",
      ],
    },
    {
      moduleNumber: 2,
      moduleTitle: "Data Structures",
      topics: ["Arrays and Lists", "Stacks and Queues", "Trees and Graphs"],
    },
  ],
  },
  //schedule
  courseSchedule: {
    classStartDate: "",
    classEndDate: "",
    midSemesterExamDate: "",
    endSemesterExamDate: "",
    classDaysAndTimes: [
      {
        day: "Monday",
        time: "6 pm to 7.30 pm",
      },
      {
        day: "Wednesday",
        time: "6 pm to 7.30 pm",
      },
    ],
  },
  //attendance
  // New array format: sessions is an array of { date, branch, sessionNumber, time, attendees, totalStudents, presentCount }
  attendance: {
    sessions: [],
  },
  students: [], // Students will be loaded from API
};

// Create the context
const CourseContext = createContext();

// Create the provider component
export const CourseProvider = ({ children }) => {
  const [courseData, setCourseData] = useState(initialCourseData);
  const [savedSessions, setSavedSessions] = useState(new Set());
  const [pendingChangesCount, setPendingChangesCount] = useState(0);

  // Calculate total credit points
  const totalCredits = Object.values(courseData.creditPoints || {}).reduce(
    (acc, curr) => acc + curr,
    0
  );

  // Existing functions
  const updateAboutCourse = (newAboutCourse) => {
    setCourseData((prev) => ({
      ...prev,
      aboutCourse: newAboutCourse,
    }));
  };

  const updateCreditPoints = (type, value) => {
    setCourseData((prev) => ({
      ...prev,
      creditPoints: {
        ...prev.creditPoints,
        [type]: value,
      },
    }));
  };

  const addLearningOutcome = (newOutcome) => {
    setCourseData((prev) => ({
      ...prev,
      learningOutcomes: [...prev.learningOutcomes, newOutcome],
    }));
  };

  const updateLearningOutcome = (index, newOutcome) => {
    setCourseData((prev) => ({
      ...prev,
      learningOutcomes: prev.learningOutcomes.map((outcome, i) =>
        i === index ? newOutcome : outcome
      ),
    }));
  };

  const removeLearningOutcome = (index) => {
    setCourseData((prev) => ({
      ...prev,
      learningOutcomes: prev.learningOutcomes.filter((_, i) => i !== index),
    }));
  };

  // New weekly plan functions
  const addWeek = () => {
    setCourseData((prev) => ({
      ...prev,
      weeklyPlan: [
        ...prev.weeklyPlan,
        {
          weekNumber: prev.weeklyPlan.length + 1,
          topics: [],
        },
      ],
    }));
  };

  const updateWeek = (weekNumber, newTopics) => {
    setCourseData((prev) => ({
      ...prev,
      weeklyPlan: prev.weeklyPlan.map((week) =>
        week.weekNumber === weekNumber ? { ...week, topics: newTopics } : week
      ),
    }));
  };

  const removeWeek = (weekNumber) => {
    setCourseData((prev) => ({
      ...prev,
      weeklyPlan: prev.weeklyPlan
        .filter((week) => week.weekNumber !== weekNumber)
        .map((week, index) => ({
          ...week,
          weekNumber: index + 1,
        })),
    }));
  };

  const reorderWeeks = (startIndex, endIndex) => {
    setCourseData((prev) => {
      const newWeeklyPlan = Array.from(prev.weeklyPlan);
      const [removed] = newWeeklyPlan.splice(startIndex, 1);
      newWeeklyPlan.splice(endIndex, 0, removed);

      // Update week numbers
      return {
        ...prev,
        weeklyPlan: newWeeklyPlan.map((week, index) => ({
          ...week,
          weekNumber: index + 1,
        })),
      };
    });
  };

  // Add a new module (handles null/legacy shapes)
  const addModule = () => {
    setCourseData((prev) => {
      const currentModules = Array.isArray(prev?.syllabus?.modules)
        ? prev.syllabus.modules
        : Array.isArray(prev?.syllabus)
        ? prev.syllabus
        : [];

      const newModule = {
        moduleNumber: currentModules.length + 1,
          moduleTitle: "New Module",
          topics: [],
      };

      return {
        ...prev,
        syllabus: {
          modules: [...currentModules, newModule],
        },
      };
    });
  };

  // Update module title
  const updateModuleTitle = (moduleNumber, newTitle) => {
    setCourseData((prev) => {
      const currentModules = Array.isArray(prev?.syllabus?.modules)
        ? prev.syllabus.modules
        : Array.isArray(prev?.syllabus)
        ? prev.syllabus
        : [];

      return {
      ...prev,
        syllabus: {
          modules: currentModules.map((module) =>
        module.moduleNumber === moduleNumber
          ? { ...module, moduleTitle: newTitle }
          : module
      ),
        },
      };
    });
  };

  // Add topic to a module
  const addTopicToModule = (moduleNumber) => {
    setCourseData((prev) => {
      const currentModules = Array.isArray(prev?.syllabus?.modules)
        ? prev.syllabus.modules
        : Array.isArray(prev?.syllabus)
        ? prev.syllabus
        : [];

      return {
      ...prev,
        syllabus: {
          modules: currentModules.map((module) =>
        module.moduleNumber === moduleNumber
          ? { ...module, topics: [...(Array.isArray(module.topics) ? module.topics : []), "New Topic"] }
          : module
      ),
        },
      };
    });
  };

  // Update topic in a module
  const updateTopic = (moduleNumber, topicIndex, newTopic) => {
    setCourseData((prev) => {
      const currentModules = Array.isArray(prev?.syllabus?.modules)
        ? prev.syllabus.modules
        : Array.isArray(prev?.syllabus)
        ? prev.syllabus
        : [];

      return {
      ...prev,
        syllabus: {
          modules: currentModules.map((module) =>
        module.moduleNumber === moduleNumber
          ? {
              ...module,
              topics: (Array.isArray(module.topics) ? module.topics : []).map((topic, index) =>
                index === topicIndex ? newTopic : topic
              ),
            }
          : module
      ),
        },
      };
    });
  };

  // Remove topic from a module
  const removeTopic = (moduleNumber, topicIndex) => {
    setCourseData((prev) => {
      const currentModules = Array.isArray(prev?.syllabus?.modules)
        ? prev.syllabus.modules
        : Array.isArray(prev?.syllabus)
        ? prev.syllabus
        : [];

      return {
      ...prev,
        syllabus: {
          modules: currentModules.map((module) =>
        module.moduleNumber === moduleNumber
          ? {
              ...module,
              topics: (Array.isArray(module.topics) ? module.topics : []).filter((_, index) => index !== topicIndex),
            }
          : module
      ),
        },
      };
    });
  };

  // Remove an entire module
  const removeModule = (moduleNumber) => {
    setCourseData((prev) => {
      const currentModules = Array.isArray(prev?.syllabus?.modules)
        ? prev.syllabus.modules
        : Array.isArray(prev?.syllabus)
        ? prev.syllabus
        : [];

      const filtered = currentModules
        .filter((module) => module.moduleNumber !== moduleNumber)
        .map((module, index) => ({ ...module, moduleNumber: index + 1 }));

      return {
      ...prev,
        syllabus: { modules: filtered },
      };
    });
  };

  // Reorder modules
  const reorderModules = (startIndex, endIndex) => {
    setCourseData((prev) => {
      const currentModules = Array.isArray(prev?.syllabus?.modules)
        ? prev.syllabus.modules
        : Array.isArray(prev?.syllabus)
        ? prev.syllabus
        : [];

      const newModules = Array.from(currentModules);
      const [removed] = newModules.splice(startIndex, 1);
      newModules.splice(endIndex, 0, removed);

      return {
        ...prev,
        syllabus: {
          modules: newModules.map((module, index) => ({
          ...module,
          moduleNumber: index + 1,
        })),
        },
      };
    });
  };

  // Update course dates
  const updateCourseDate = (dateType, newDate) => {
    setCourseData((prev) => ({
      ...prev,
      courseSchedule: {
        ...prev.courseSchedule,
        [dateType]: newDate,
      },
    }));
  };

  // Add new class day and time
  const addClassDayAndTime = (newDay, newTime) => {
    setCourseData((prev) => ({
      ...prev,
      courseSchedule: {
        ...prev.courseSchedule,
        classDaysAndTimes: [
          ...prev.courseSchedule.classDaysAndTimes,
          { day: newDay, time: newTime },
        ],
      },
    }));
  };

  // Update existing class day and time
  const updateClassDayAndTime = (index, updatedDay, updatedTime) => {
    setCourseData((prev) => ({
      ...prev,
      courseSchedule: {
        ...prev.courseSchedule,
        classDaysAndTimes: prev.courseSchedule.classDaysAndTimes.map(
          (item, i) =>
            i === index ? { day: updatedDay, time: updatedTime } : item
        ),
      },
    }));
  };

  // Remove class day and time
  const removeClassDayAndTime = (index) => {
    setCourseData((prev) => ({
      ...prev,
      courseSchedule: {
        ...prev.courseSchedule,
        classDaysAndTimes: prev.courseSchedule.classDaysAndTimes.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  // Helper: find a session index by date, time, and branch in the sessions array
  const findSessionIndex = (sessions, date, time, branch) => {
    const dateStr = date.includes("T") ? date.split("T")[0] : date;
    return sessions.findIndex((s) => {
      const sDate = (s.date || "").includes("T") ? s.date.split("T")[0] : s.date;
      const branchMatch = !branch || s.branch === branch;
      return sDate === dateStr && s.time === time && branchMatch;
    });
  };

  // Helper: build a session key string for savedSessions tracking
  const getSessionKey = (session) => {
    const dateStr = (session.date || "").includes("T")
      ? session.date.split("T")[0]
      : session.date;
    return `${dateStr}_${session.time || ""}_${session.branch || ""}`;
  };

  // Create a new attendance session for a specific date, time, and branch
  const createAttendanceSession = (date, time, branch) => {
    setCourseData((prev) => {
      const sessions = Array.isArray(prev.attendance?.sessions) ? prev.attendance.sessions : [];
      const existingIdx = findSessionIndex(sessions, date, time, branch);

      // Session already exists, no change
      if (existingIdx !== -1) return prev;

      return {
        ...prev,
        attendance: {
          ...prev.attendance,
          sessions: [
            ...sessions,
            {
              date: date,
              branch: branch || "",
              sessionNumber: "1",
              time: time,
              attendees: [],
              totalStudents: 0,
              presentCount: 0,
            },
          ],
        },
      };
    });
  };

  // Mark a student as present for a session
  const markStudentPresent = (date, time, branch, studentId) => {
    let changesMade = false;

    setCourseData((prev) => {
      const sessions = Array.isArray(prev.attendance?.sessions) ? prev.attendance.sessions : [];
      const idx = findSessionIndex(sessions, date, time, branch);

      if (idx === -1) {
        // Session doesn't exist, create it with the student
        changesMade = true;
        return {
          ...prev,
          attendance: {
            ...prev.attendance,
            sessions: [
              ...sessions,
              {
                date: date,
                branch: branch || "",
                sessionNumber: "1",
                time: time,
                attendees: [studentId],
                totalStudents: 0,
                presentCount: 1,
              },
            ],
          },
        };
      }

      const session = sessions[idx];
      // Already present
      if (session.attendees && session.attendees.includes(studentId)) return prev;

      changesMade = true;
      const updatedSessions = [...sessions];
      const newAttendees = [...(session.attendees || []), studentId];
      updatedSessions[idx] = {
        ...session,
        attendees: newAttendees,
        presentCount: newAttendees.length,
      };

      return {
        ...prev,
        attendance: { ...prev.attendance, sessions: updatedSessions },
      };
    });

    // Increment pending changes counter if a change was made
    if (changesMade) {
      setPendingChangesCount((prev) => prev + 1);
    }
  };

  // Mark a student as absent (remove from attendance)
  const markStudentAbsent = (date, time, branch, studentId) => {
    let changesMade = false;

    setCourseData((prev) => {
      const sessions = Array.isArray(prev.attendance?.sessions) ? prev.attendance.sessions : [];
      const idx = findSessionIndex(sessions, date, time, branch);

      if (idx === -1) return prev;

      const session = sessions[idx];
      // Check if student is actually in the attendees list
      if (!session.attendees || !session.attendees.includes(studentId)) return prev;

      changesMade = true;
      const updatedSessions = [...sessions];
      const newAttendees = (session.attendees || []).filter((id) => id !== studentId);
      updatedSessions[idx] = {
        ...session,
        attendees: newAttendees,
        presentCount: newAttendees.length,
      };

      return {
        ...prev,
        attendance: { ...prev.attendance, sessions: updatedSessions },
      };
    });

    // Increment pending changes counter if a change was made
    if (changesMade) {
      setPendingChangesCount((prev) => prev + 1);
    }
  };

  // Get attendance for a specific session
  const getSessionAttendance = (date, time, branch) => {
    const sessions = Array.isArray(courseData.attendance?.sessions) ? courseData.attendance.sessions : [];
    const idx = findSessionIndex(sessions, date, time, branch);
    return idx !== -1 ? (sessions[idx].attendees || []) : [];
  };

  // Remove an entire attendance session
  const removeAttendanceSession = (date, time, branch) => {
    setCourseData((prev) => {
      const sessions = Array.isArray(prev.attendance?.sessions) ? prev.attendance.sessions : [];
      const idx = findSessionIndex(sessions, date, time, branch);

      if (idx === -1) return prev;

      return {
        ...prev,
        attendance: {
          ...prev.attendance,
          sessions: sessions.filter((_, i) => i !== idx),
        },
      };
    });
  };

  // Get attendance rate for a specific student (optionally filtered by branch)
  const getStudentAttendanceRate = (studentId, branch) => {
    const allSessions = Array.isArray(courseData.attendance?.sessions) ? courseData.attendance.sessions : [];
    const sessions = branch
      ? allSessions.filter((s) => s.branch === branch)
      : allSessions;
    const totalSessions = sessions.length;

    if (totalSessions === 0) return 0;

    const attendedSessions = sessions.filter((session) =>
      (session.attendees || []).includes(studentId)
    ).length;

    return (attendedSessions / totalSessions) * 100;
  };

  // Mark sessions as saved (called after successful backend save)
  const markSessionsAsSaved = (savedSessionsList) => {
    setSavedSessions(new Set(savedSessionsList || []));
  };

  // Reset the pending changes counter (called after successful save)
  const resetPendingChanges = () => {
    setPendingChangesCount(0);
  };

  const value = {
    courseData,
    totalCredits,
    setCourseData,
    savedSessions,
    pendingChangesCount,
    updateAboutCourse,
    updateCreditPoints,
    addLearningOutcome,
    updateLearningOutcome,
    removeLearningOutcome,
    // Add new weekly plan functions to context
    addWeek,
    updateWeek,
    removeWeek,
    reorderWeeks,
    // new syllabus
    addModule,
    updateModuleTitle,
    addTopicToModule,
    updateTopic,
    removeTopic,
    removeModule,
    reorderModules,
    // schedule
    updateCourseDate,
    addClassDayAndTime,
    updateClassDayAndTime,
    removeClassDayAndTime,
    // attendance
    createAttendanceSession,
    markStudentPresent,
    markStudentAbsent,
    getSessionAttendance,
    removeAttendanceSession,
    getStudentAttendanceRate,
    markSessionsAsSaved,
    resetPendingChanges,
    getSessionKey,
  };

  return (
    <CourseContext.Provider value={value}>{children}</CourseContext.Provider>
  );
};

// Custom hook for using the course context
export const useCourse = () => {
  const context = useContext(CourseContext);
  if (!context) {
    throw new Error("useCourse must be used within a CourseProvider");
  }
  return context;
};

export default CourseContext;
